import React, { FC, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { useSearchParams } from 'react-router-dom';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { notificationsService } from '@web/services/singletons';
import { Notification, NotificationTypeId } from '@packages/models/api';
import {
    useNotificationsState,
    fetchNotificationsList,
    useNotificationsDispatch,
} from '@packages/contexts/notifications';
import { useAbortController } from '@packages/core/http';

interface SecurityCodeBannerProps {
    expiresAt: Date;
    onButtonClick: () => void;
    buttonText?: 'openVinSession' | 'showAuthorizationCode';
    showTimer?: boolean;
}

const useStyles = createUseStyles({
    '@keyframes fadeOut': {
        from: {
            opacity: 1,
            transform: 'translateY(0)',
            maxHeight: '200px',
        },
        to: {
            opacity: 0,
            transform: 'translateY(-10px)',
            maxHeight: '0px',
        },
    },
    '@keyframes highlight': {
        '0%, 100%': {
            boxShadow: '0 0 0 0 rgba(245, 197, 24, 0)',
            borderColor: '#F5C518',
            backgroundColor: '#FEF9E7',
        },
        '50%': {
            boxShadow: '0 0 20px 8px rgba(245, 197, 24, 0.7), inset 0 0 15px rgba(245, 197, 24, 0.3)',
            borderColor: '#E6B800',
            backgroundColor: '#FFF3B0',
        },
    },
    bannerWrapper: {
        overflow: 'hidden',
        marginBottom: 32,
        transition: 'margin-bottom 0.5s ease-out',
    },
    bannerWrapperExpiring: {
        marginBottom: 0,
    },
    banner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#FEF9E7',
        border: `1px solid #F5C518`,
        borderRadius: 8,
        padding: '16px 24px',
        gap: 16,
    },
    bannerExpiring: {
        animation: '$fadeOut 0.5s ease-out forwards',
    },
    bannerHighlight: {
        animation: '$highlight 0.5s ease-in-out 5',
    },
    contentWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        flex: 1,
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    textWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    title: {
        overflow: 'hidden',
        color: '#071424',
        textOverflow: 'ellipsis',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '160%',
    },
    description: {
        overflow: 'hidden',
        color: '#4C525D',
        textOverflow: 'ellipsis',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '160%',
    },
    timer: {
        overflow: 'hidden',
        color: '#4C525D',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '160%',
    },
    timerValue: {
        color: '#4C525D',
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '160%',
    },
    button: {
        flexShrink: 0,
    },
});

const formatTimeRemaining = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '00 : 00 : 00';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

export const SecurityCodeBanner: FC<SecurityCodeBannerProps> = ({
    expiresAt,
    onButtonClick,
    buttonText = 'openVinSession',
    showTimer = true,
}) => {
    const classes = useStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const bannerRef = useRef<HTMLDivElement>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(0);
    const [isExpiring, setIsExpiring] = useState<boolean>(false);
    const [shouldRender, setShouldRender] = useState<boolean>(true);
    const [isHighlighting, setIsHighlighting] = useState<boolean>(false);

    const buttonLabel = buttonText === 'showAuthorizationCode' ? 'Show authorization code' : 'Open VIN Session';

    useEffect(() => {
        const triggerHighlight = () => {
            if (bannerRef.current) {
                bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

                setIsHighlighting(true);

                setTimeout(() => {
                    setIsHighlighting(false);
                }, 2500);
            }
        };

        const handleHighlightEvent = () => {
            triggerHighlight();
        };
        window.addEventListener('highlightSecurityBanner', handleHighlightEvent);

        const shouldHighlight = searchParams.get('highlightBanner') === 'true';
        if (shouldHighlight && bannerRef.current) {
            triggerHighlight();
            window.dispatchEvent(new CustomEvent('openAuthorizationCodeModal'));
            searchParams.delete('highlightBanner');
            setSearchParams(searchParams, { replace: true });
        }

        return () => {
            window.removeEventListener('highlightSecurityBanner', handleHighlightEvent);
        };
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!showTimer) {
            setTimeRemaining(0);
            return;
        }

        let removeTimer: NodeJS.Timeout | null = null;

        const calculateTimeRemaining = () => {
            const now = Date.now();
            const expiryTime = expiresAt.getTime();
            const diff = Math.max(0, Math.floor((expiryTime - now) / 1000));
            setTimeRemaining(diff);

            if (diff <= 0 && !isExpiring) {
                setIsExpiring(true);
                removeTimer = setTimeout(() => {
                    setShouldRender(false);
                }, 500);
            }
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => {
            clearInterval(interval);
            if (removeTimer) clearTimeout(removeTimer);
        };
    }, [expiresAt, isExpiring, showTimer]);

    if (!shouldRender) {
        return null;
    }

    const bannerClasses = [
        classes.banner,
        isExpiring ? classes.bannerExpiring : '',
        isHighlighting ? classes.bannerHighlight : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div ref={bannerRef} className={`${classes.bannerWrapper} ${isExpiring ? classes.bannerWrapperExpiring : ''}`}>
            <div className={bannerClasses}>
                <div className={classes.contentWrapper}>
                    <div className={classes.iconWrapper}>
                        <Icon name="key" size={24} color="yellowOne" />
                    </div>
                    <div className={classes.textWrapper}>
                        <span className={classes.title}>Car key reprogramming required</span>
                        <span className={classes.description}>
                            A time-limited security code has been issued and assigned to you.
                        </span>
                        {showTimer && (
                            <span className={classes.timer}>
                                Expires in{' '}
                                <span className={classes.timerValue}>{formatTimeRemaining(timeRemaining)}</span>
                            </span>
                        )}
                    </div>
                </div>
                <div className={classes.button}>
                    <Button
                        variant="primary"
                        onPress={onButtonClick}
                        iconRight={<Icon size={16} name="chevron-right" color="white" />}
                    >
                        {buttonLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const useMockSecurityCode = () => {
    const mockData = useMemo(() => {
        const mockExpiresAt = new Date();
        mockExpiresAt.setHours(mockExpiresAt.getHours());
        mockExpiresAt.setMinutes(mockExpiresAt.getMinutes());
        mockExpiresAt.setSeconds(mockExpiresAt.getSeconds() + 9);

        return {
            hasActiveSecurityCode: true,
            expiresAt: mockExpiresAt,
            vinData: {
                vin: 'JF1ZDBS14S9701240',
                modelYear: '2021',
                model: 'Outback',
                imageUrl: undefined,
            },
        };
    }, []);

    return mockData;
};

export interface OtpwNotificationData {
    vin: string;
    expiresAt: string;
    accountName: string;
    notificationId: string;
    notificationTypeId: string;
    actionUrl: string;
    timestampDescription: string;
    otpwAssignmentId: string;
}

export interface ActiveOtpwVin {
    vin: string;
    expiresAt: Date;
    otpwAssignmentId: string;
}

export interface SecurityCodeNotification {
    hasActiveSecurityCode: boolean;
    expiresAt: Date | null;
    notification: Notification | null;
    otpwData: OtpwNotificationData | null;
    otpwAssignmentId: string | null;
    activeVins: ActiveOtpwVin[];
    hasMultipleVins: boolean;
    isLoading: boolean;
    refetch: () => Promise<void>;
}

export const useSecurityCodeNotification = (): SecurityCodeNotification => {
    const { abortSignalRef } = useAbortController();
    const { notifications, notificationsFetched } = useNotificationsState();
    const notificationsDispatch = useNotificationsDispatch();

    const [hasActiveSecurityCode, setHasActiveSecurityCode] = useState(false);

    const otpwNotifications = useMemo(() => {
        const now = new Date();
        return notifications.filter((n) => {
            if (n.notificationTypeId !== NotificationTypeId.OtpwAssignment) return false;
            const data = n.data as OtpwNotificationData;
            if (data?.expiresAt) {
                return new Date(data.expiresAt) > now;
            }
            return false;
        });
    }, [notifications]);

    const otpwNotification = otpwNotifications[0] || null;

    const otpwData = useMemo(() => {
        if (!otpwNotification?.data) return null;
        return otpwNotification.data as OtpwNotificationData;
    }, [otpwNotification]);

    const activeVins = useMemo((): ActiveOtpwVin[] => {
        return otpwNotifications.map((n) => {
            const data = n.data as OtpwNotificationData;
            return {
                vin: data.vin,
                expiresAt: new Date(data.expiresAt),
                otpwAssignmentId: data.otpwAssignmentId,
            };
        });
    }, [otpwNotifications]);

    const expiresAt = useMemo(() => {
        if (!otpwData?.expiresAt) return null;
        return new Date(otpwData.expiresAt);
    }, [otpwData]);

    const hasMultipleVins = activeVins.length > 1;

    const earliestExpiry = useMemo(() => {
        if (activeVins.length === 0) return null;
        return activeVins.reduce(
            (earliest, vin) => (vin.expiresAt < earliest ? vin.expiresAt : earliest),
            activeVins[0].expiresAt
        );
    }, [activeVins]);

    const refetch = useCallback(async () => {
        await fetchNotificationsList({
            notificationsService,
            notificationsDispatch,
            signal: abortSignalRef.current,
            ignoreCache: true,
        });
    }, [notificationsDispatch, abortSignalRef]);

    useEffect(() => {
        if (!earliestExpiry) {
            setHasActiveSecurityCode(false);
            return;
        }

        const checkExpiry = () => {
            const hasActive = activeVins.length > 0 && activeVins.some((v) => v.expiresAt > new Date());
            setHasActiveSecurityCode(hasActive);
            return hasActive;
        };

        if (!checkExpiry()) return;
        const timeUntilExpiry = earliestExpiry.getTime() - Date.now();

        const timer = setTimeout(() => {
            refetch();
        }, timeUntilExpiry + 100);

        return () => clearTimeout(timer);
    }, [earliestExpiry, refetch, activeVins]);

    const otpwAssignmentId = useMemo(() => {
        return otpwData?.otpwAssignmentId || null;
    }, [otpwData]);

    return {
        hasActiveSecurityCode,
        expiresAt,
        notification: otpwNotification,
        otpwData,
        otpwAssignmentId,
        activeVins,
        hasMultipleVins,
        isLoading: !notificationsFetched,
        refetch,
    };
};
